import React from 'react';
import { View, TouchableOpacity, StyleSheet, Switch } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';

interface OptionCardProps {
  title: string;
  subtitle: string;
  value: boolean;
  onPress: () => void;
  type?: 'switch' | 'radio' | 'checkbox';
  disabled?: boolean;
}

export const OptionCard: React.FC<OptionCardProps> = ({
  title,
  subtitle,
  value,
  onPress,
  type = 'switch',
  disabled = false,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} activeOpacity={0.7} style={styles.touchableWrapper}>
      <GlassCard
        padding={0}
        style={[
          styles.cardShell,
          value && styles.cardShellActive,
          disabled && styles.cardShellDisabled,
        ]}
      >
        <View style={styles.content}>
          <View style={styles.textContainer}>
            <Text style={[styles.title, disabled && styles.textDisabled]}>{title}</Text>
            <Text style={[styles.subtitle, disabled && styles.textDisabled]}>{subtitle}</Text>
          </View>

          <View style={styles.controlContainer}>
            {type === 'switch' ? (
              <Switch
                value={value}
                onValueChange={onPress}
                trackColor={{
                  false: admin.dataTable.border,
                  true: admin.formInput.borderFocused,
                }}
                thumbColor={value ? '#FFFFFF' : '#f4f3f4'}
                ios_backgroundColor={admin.dataTable.border}
                disabled={disabled}
              />
            ) : type === 'checkbox' ? (
              <View style={[styles.checkbox, value && styles.checkboxActive]}>
                {value && (
                  <SVG_COMPONENTS.CHECK_CIRCLE
                    width={16}
                    height={16}
                    color={admin.formInput.borderFocused}
                  />
                )}
              </View>
            ) : (
              <View style={[styles.radio, value && styles.radioActive]}>
                {value && <View style={styles.radioInner} />}
              </View>
            )}
          </View>
        </View>
      </GlassCard>
    </TouchableOpacity>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      touchableWrapper: {
        marginBottom: 12,
      },
      cardShell: {
        minHeight: 160,
      },
      cardShellActive: {
        borderColor: admin.formInput.borderFocused,
        backgroundColor: admin.selector.itemSelectedBg,
      },
      cardShellDisabled: {
        opacity: 0.6,
      },
      content: {
        padding: 16,
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
      },
      textContainer: {
        flex: 1,
        marginRight: 16,
        justifyContent: 'center',
      },
      title: {
        fontSize: 16,
        fontWeight: '500',
        color: admin.dataTable.text,
        marginBottom: 4,
      },
      subtitle: {
        fontSize: 12,
        color: colors.text.dim,
        lineHeight: 16,
      },
      textDisabled: {
        color: colors.text.muted,
      },
      controlContainer: {
        alignItems: 'center',
        justifyContent: 'center',
      },
      radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: admin.dataTable.border,
        alignItems: 'center',
        justifyContent: 'center',
      },
      radioActive: {
        borderColor: admin.formInput.borderFocused,
      },
      radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: admin.formInput.borderFocused,
      },
      checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: admin.dataTable.border,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
      },
      checkboxActive: {
        borderColor: admin.formInput.borderFocused,
        backgroundColor: 'transparent',
      },
    });
  });
